/**
 * The Free Rounds controller.
 *
 * @class Core.Slots.FreeRoundsController
 * @augments Core.Controller
 */
Core.Slots.FreeRoundsController = {
    REQUEST_REASON: {
        WIDGET: "widget",
        REJECT: "reject",
        GENERAL: "general"
    },

    FREE_ROUNDS_TYPES: {
        EXTRA: "EXTRA",
        NEW_PROGRAM: "NEW_PROGRAM",
        WIDGET: "WIDGET",
        RELOAD: "RELOAD",
        NONE: "NONE"
    },

    constructor: function() {
        Core.Slots.FreeRoundsController.superclass.constructor.apply(
            this,
            arguments
        );

        this.requestReason = this.REQUEST_REASON.GENERAL;
        this.nextFreeRoundsType = this.FREE_ROUNDS_TYPES.NONE;

        this.hasShownSplash = false;
        this.totalFreeRounds = -1;
    },

    /**
     * @protected
     * @inheritdoc
     */
    init: function() {
        Core.Slots.FreeRoundsController.superclass.init.apply(this, arguments);
    },

    /**
     * @protected
     * @inheritdoc
     */
    setupEvents: function() {
        this.on({
            "notify:services.init": this.onServicesInited,
            "notify:stateHandler.enteringSetupFreeRoundsState": this
                .onEnteringSetupFreeRounds,
            "notify:stateHandler.enteringExtraFreeRoundsState": this
                .onEnteringExtraFreeRounds,
            "notify:stateHandler.leavingExtraFreeRoundsState": this
                .onLeavingExtraFreeRounds,
            "notify:stateHandler.enteringEndFreeRoundsState": this
                .endSingleFreeRoundsProgram,
            "notify:stateHandler.leavingEndFreeRoundsState": this
                .checkNextSingleFreeRoundsProgram,
            "notify:stateHandler.enteringCheckForAdditionalFreeRoundsState": this
                .requestGameConfiguration,
            "notify:stateHandler.enteringAdditionalFreeRoundsState": this
                .onEnteringAdditionalFreeRounds,
            // fired in Bonus Widget Module (Origin)
            "request:freeRounds.requestGameConfiguration": this.requestGameConfiguration.bind(
                this,
                this.REQUEST_REASON.WIDGET
            ),
            "notify:responseParser.gameConfigurationParsed": this
                .onGameConfigurationParsed
        });
    },

    onServicesInited: function() {
        Services.freeRounds.on("started", this.startFreeRounds.bind(this));
        Services.freeRounds.on("ended", this.onFreeRoundsEnded.bind(this));
        Services.freeRounds.on(
            "confirmationResolved",
            this.onConfirmationResolved.bind(this)
        );
    },

    /**
     * Sets up the game for free rounds.
     *
     * @private
     * @returns {void}
     */
    startFreeRounds: function() {
        this.addListener(
            "notify:stateHandler.enteringIdleState",
            this.onEnteringIdleState
        );
        this.addListener(
            "notify:stateHandler.leavingIdleState",
            this.onLeavingIdleState
        );

        this.freeRoundsStarted = true;
        this.hasShownSplash = true;
        this.totalFreeRounds = Services.freeRounds.getRemaining();
    },

    /**
     * Module reaction to FR service 'ended' event.
     *
     * @private
     * @returns {void}
     */
    onFreeRoundsEnded: function() {
        // Stop listening to game round event since we're done with free rounds
        this.removeListener("notify:stateHandler.enteringIdleState");
        this.removeListener("notify:stateHandler.leavingIdleState");

        this.freeRoundsStarted = false;

        this.closeFreeRoundsDialog();

        this.fireEvent("notify:freeRounds.programEnded");

        // init widget in the middle of 'checkForAdditionalFreeRounds' state
        if (this.nextFreeRoundsType === this.FREE_ROUNDS_TYPES.WIDGET) {
            this.fireEvent(
                "notify:bonusProgramWidget.reInit",
                Resources.readData("gameServerInitResponse")
            );
        }
    },

    /**
     * Updates FR depended entities.
     *
     * @private
     * @param {Object} response The unparsed response.
     * @returns {void}
     */
    updateFreeRoundsEntities: function(response) {
        Services.win.updateResponse(response);
        Services.bet._freeRoundsSettingsUpdate();
        Services.moneyManager.onFreeRoundsReInit();
        Services.freeRounds._onReInitResponseReceived(response);
    },

    /**
     * Updates local storage with FR counter.
     *
     * @private
     * @returns {void}
     */
    onEnteringIdleState: function() {
        this.requestCounterUpdate();
    },

    /**
     * Sets total/remaining FR rounds on leaving idle state.
     *
     * @private
     * @returns {void}
     */
    onLeavingIdleState: function() {
        this.notifyFreeRoundsLeft();
    },

    /**
     * Prepares new FR program (shows dialog or start bonus widget).
     *
     * @private
     * @returns {void}
     */
    onEnteringSetupFreeRounds: function() {
        if (this.nextFreeRoundsType === this.FREE_ROUNDS_TYPES.WIDGET) {
            this.fireEvent(
                "notify:bonusProgramWidget.reInit",
                Resources.readData("gameServerInitResponse")
            );
        }
        else {
            this.showConfirmationDialog();
        }
    },

    /**
     * Prepares extra FR rounds.
     *
     * @private
     * @returns {void}
     */
    onEnteringExtraFreeRounds: function() {
        this.showExtraFreeRoundsDialog();
        Services.freeRounds.notifyExtraRounds();
    },

    /**
     * Starts FreeRounds after extra rounds confirmation.
     *
     * @private
     * @returns {void}
     */
    onLeavingExtraFreeRounds: function() {
        Services.freeRounds.start();
    },

    /**
     * Stops additionalFreeRounds state if bonus program widget isn't active.
     *
     * @private
     * @returns {void}
     */
    onEnteringAdditionalFreeRounds: function() {
        if (this.requestReason !== this.REQUEST_REASON.WIDGET) {
            this.fireEvent("notify:freeRounds.additionalFreeRoundsProcessed");
        }
    },

    /**
     * Sets/updates FR data if the new FR program is available.
     *
     * @private
     * @returns {void}
     */
    checkNextSingleFreeRoundsProgram: function() {
        if (this.nextFreeRoundsType === this.FREE_ROUNDS_TYPES.NEW_PROGRAM) {
            this.updateFreeRoundsEntities(
                Resources.readData("gameServerInitResponseObject")
            );
        }
    },

    /**
     * Processes Free Rounds confirmation.
     *
     * @private
     * @returns {void}
     */
    onConfirmationResolved: function() {
        if (Services.freeRounds.isAccepted()) {
            this.fireEvent("notify:freeRounds.setupFinished");
        }
        else if (Services.freeRounds.isRejected()) {
            this.requestGameConfiguration(this.REQUEST_REASON.REJECT);
        }
    },

    /**
     * Update the free rounds left counter.
     *
     * @returns {void}
     */
    notifyFreeRoundsLeft: function() {
        var freeRoundsLeft = Services.freeRounds.getRemaining();

        Services.freeRounds.notifyFreeRoundsLeft(
            this.totalFreeRounds,
            freeRoundsLeft
        );
    },

    /**
     * Request new game information from the server.
     *
     * @param {string} [src=null] Shows the initiator of request.
     * @private
     * @returns {void}
     */
    requestGameConfiguration: function(src) {
        this.requestReason = src || this.REQUEST_REASON.GENERAL;

        // Stop autoplay since we're out of rounds
        this.fireEvent("request:autoPlayer.stop");

        // Set free rounds mode to false since we don't know yet if we will get more.
        Services.freeRounds.setExtraParam("freeroundmode", false);

        this.nextFreeRoundsType = this.FREE_ROUNDS_TYPES.NONE;

        this.fireEvent(
            "request:serverManager.sendAction",
            "init",
            "notify:responseParser.gameConfigurationParsed",
            "notify:serverManager.reInitRequestFailed"
        );
    },

    /**
     * Handler for storing new data on new init-response.
     *
     * @private
     * @param {Object} parsedResponse The parsed response, unused here but included to be able to use responseParser event.
     * @param {Object} response The unparsed response.
     * @returns {void}
     */
    onGameConfigurationParsed: function(parsedResponse, response) {
        this.hasShownSplash = false;

        // NOTE: due to multiple inconsistencies with events flow and services usage - we need to store init response asap
        this.storeNewServerResponse(parsedResponse, response);

        switch (this.requestReason) {
            case this.REQUEST_REASON.WIDGET: {
                this._onGameConfigurationParsedWidget(parsedResponse);
                break;
            }

            case this.REQUEST_REASON.REJECT: {
                this.updateFreeRoundsEntities(parsedResponse);
                this.fireEvent("notify:freeRounds.setupFinished");
                break;
            }

            case this.REQUEST_REASON.GENERAL:
            default: {
                this._onGameConfigurationParsedGeneral(parsedResponse);
            }
        }

        this.fireEvent(
            "notify:freeRounds.reInitResponseReceived",
            parsedResponse
        );
    },

    /**
     * Stores new server init-response to Resources.
     *
     * @private
     * @param {Object} parsedResponse The parsed response, unused here but included to be able to use responseParser event.
     * @param {Object} response The unparsed response.
     * @returns {void}
     */
    storeNewServerResponse: function(parsedResponse, response) {
        Resources.storeData(
            "gameServerInitResponse",
            Sys.utils.queryStringToObject(response.responseText)
        );
        Resources.storeData("gameServerInitResponseObject", parsedResponse);
        Resources.storeData(
            "unParsedGameServerInitResponse",
            response.responseText
        );
    },

    /**
     * Handles server init-response initiated by bonus widget.
     *
     * @private
     * @param {Object} parsedResponse The parsed response, unused here but included to be able to use responseParser event.
     * @returns {void}
     */
    _onGameConfigurationParsedWidget: function(parsedResponse) {
        this.updateFreeRoundsEntities(parsedResponse);

        Services.freeRounds.acceptFreeRounds();
        Services.freeRounds.start();

        Resources.removeData("bonusProgramWidgetActive");

        this.fireEvent("notify:freeRounds.additionalFreeRoundsProcessed");
    },

    /**
     * General handler of server init-response.
     *
     * @private
     * @param {Object} parsedResponse The parsed response, unused here but included to be able to use responseParser event.
     * @returns {void}
     */
    _onGameConfigurationParsedGeneral: function(parsedResponse) {
        // Used in close dialog when a widget is detected in reInit after other free round programs.
        this.bonusProgramWidgetQueued =
            Boolean(parsedResponse.freeRoundWidgetEnabled) &&
            Services.freeRounds.getExtraParam("wantsfreerounds");
        this.bonusProgramWidgetActive = Boolean(
            Resources.readData("bonusProgramWidgetActive")
        );

        if (this.bonusProgramWidgetQueued) {
            this.bonusProgramWidgetQueued = false;
            this.nextFreeRoundsType = this.FREE_ROUNDS_TYPES.WIDGET;
            this.showFreeRoundsEndDialog();
        }
        else if (
            // On OSS there is no functionality to proceed with new Free Rounds.
            // parsedResponse is the requested response from notify:responseParser.gameConfigurationParsed.
            !Resources.readData("isOSSIntegration") &&
            Sys.isNumber(parsedResponse.freeRoundsLeft) &&
            parsedResponse.freeRoundsLeft > 0
        ) {
            this.proceedNewFreeRoundsParsed(parsedResponse);
        }
        else {
            this.proceedNoMoreFreeRoundsParsed(parsedResponse);
        }
    },

    /**
     * Proceeds init-response with new Free Rounds.
     *
     * @private
     * @param {Object} parsedResponse The parsed response, unused here but included to be able to use responseParser event.
     * @returns {void}
     */
    proceedNewFreeRoundsParsed: function(parsedResponse) {
        if (parsedResponse.isLowWinFreeRound) {
            this.nextFreeRoundsType = this.FREE_ROUNDS_TYPES.EXTRA;
            this.updateFreeRoundsEntities(parsedResponse);
        }
        else {
            this.nextFreeRoundsType = this.FREE_ROUNDS_TYPES.NEW_PROGRAM;
        }
    },

    /**
     * Proceeds init-response with no new Free Rounds.
     *
     * @private
     * @param {Object} parsedResponse The parsed response, unused here but included to be able to use responseParser event.
     * @returns {void}
     */
    proceedNoMoreFreeRoundsParsed: function(parsedResponse) {
        var nextActionIsValid = parsedResponse.nextaction === "spin";

        if (nextActionIsValid) {
            this.nextFreeRoundsType = this.FREE_ROUNDS_TYPES.NONE;
        }
        else {
            this.nextFreeRoundsType = this.FREE_ROUNDS_TYPES.RELOAD;
        }
    },

    /**
     * Ends free rounds and resets the game to normal mode.
     *
     * @private
     * @returns {void}
     */
    endSingleFreeRoundsProgram: function() {
        // Handle end of FR and SG when in OSS integration
        if (Resources.readData("isOSSIntegration")) {
            if (Services.integration.isInSpinGift()) {
                Services.integration.handleSpinGiftDialog(Utils.CONSTANTS.DIALOG_NAMES.FREE_ROUNDS_END);
            }
            else {
                Services.integration.handleFreeRoundsDialog(Utils.CONSTANTS.DIALOG_NAMES.FREE_ROUNDS_END);
            }

            return;
        }

        switch (this.nextFreeRoundsType) {
            case this.FREE_ROUNDS_TYPES.RELOAD: {
                // if server response contains no FR but next action isn't 'spin' - let's reload the game
                // the situation when user has got FR in the middle of the feature (respins, freespins) and game was reloaded
                this.showFreeRoundsReloadDialog();
                break;
            }

            default: {
                this.showFreeRoundsEndDialog();
            }
        }

        Services.freeRounds.notifySingleProgramEnded();
    },

    /**
     * Requests an update to the free rounds counter.
     *
     * @private
     * @returns {void}
     */
    requestCounterUpdate: function() {
        Services.storage.storeData(
            "freeRounds.roundsLeft",
            Services.freeRounds.getRemaining()
        );
    },

    /**
     * Fires event to close free rounds dialog.
     * Calls bonus program widget if widget program is available.
     *
     * @private
     * @returns {void}
     */
    closeFreeRoundsDialog: function() {
        this.fireEvent("notify:freeRounds.dialogClosed");
    },

    /**
     * Shows confirmation dialog (before Free Rounds started).
     *
     * @private
     * @returns {void}
     */
    showConfirmationDialog: function() {
        var me = this;

        if (Resources.readData("isOSSIntegration")) {
            // Spin Gift will be started when receive message from OSS.
            if (!Services.integration.isInSpinGift()) {
                Services.integration.handleFreeRoundsDialog(Utils.CONSTANTS.DIALOG_NAMES.FREE_ROUNDS_START);
            }

            return;
        }

        me.requestDialog({
            name: Utils.CONSTANTS.DIALOG_NAMES.FREE_ROUNDS_START,
            title: Services.legacy.languageManager.getText(
                Language.Keys.roundsLeft
            ),
            text: Services.legacy.languageManager.getText(
                Language.Keys.haveFreeRounds,
                [Services.freeRounds.getRemaining()]
            ),
            buttons: [
                {
                    primary: true,
                    scope: this,
                    text: Services.legacy.languageManager.getText(
                        Language.Keys.continue
                    ),
                    action: function() {
                        Services.freeRounds.acceptFreeRounds();
                        Services.freeRounds.start();
                        Services.freeRounds.confirmationResolved();
                    },
                    logData: {
                        name: "freeRoundsStart",
                        button: "continue"
                    },
                    buttonId: "Continue"
                }
            ]
        });
    },

    /**
     * Show a dialog when free rounds ends.
     *
     * @private
     * @returns {void}
     */
    showFreeRoundsEndDialog: function() {
        var me = this;

        me.requestDialog({
            name: Utils.CONSTANTS.DIALOG_NAMES.FREE_ROUNDS_END,
            title: Services.legacy.languageManager.getText(
                Language.Keys.freeRoundsFinished
            ),
            text: Services.legacy.languageManager.getText(
                Language.Keys.roundsUseAcctMoney
            ),
            buttons: [
                {
                    scope: this,
                    text: Services.legacy.languageManager.getText(
                        Language.Keys.continue
                    ),
                    action: function() {
                        Services.freeRounds.end();
                    },
                    logData: {
                        name: "freeRoundsEnd",
                        button: "continue"
                    },
                    buttonId: "Continue"
                }
            ]
        });
    },

    /**
     * Show a dialog stating user has won new free rounds.
     *
     * @private
     * @returns {void}
     */
    showExtraFreeRoundsDialog: function() {
        // Use default String for extra free rounds (Not present in language file)
        var me = this;

        me.requestDialog({
            name: Utils.CONSTANTS.DIALOG_NAMES.FREE_ROUNDS_EXTRA,
            title: Services.legacy.languageManager.getText(
                Language.Keys.freeRoundsExtraTitle
            ),
            text: Services.legacy.languageManager.getText(
                Language.Keys.freeRoundsExtraWon,
                [Services.freeRounds.getRemaining()]
            ),
            buttons: [
                {
                    scope: this,
                    text: Services.legacy.languageManager.getText(
                        Language.Keys.continue
                    ),
                    action: function() {
                        me.closeFreeRoundsDialog();
                    },
                    logData: {
                        name: "freeRoundsExtra",
                        button: "continue"
                    },
                    buttonId: "Continue"
                }
            ]
        });
    },

    /**
     * Show a dialog stating that free rounds has ended and forces  a reload of the game.
     *
     * @private
     * @returns {void}
     */
    showFreeRoundsReloadDialog: function() {
        var me = this;

        me.requestDialog({
            name: Utils.CONSTANTS.DIALOG_NAMES.FREE_ROUNDS_RELOAD,
            title: Services.legacy.languageManager.getText(
                Language.Keys.freeRoundsFinished
            ),
            text:
                Services.legacy.languageManager.getText(
                    Language.Keys.roundsUseAcctMoney
                ) +
                " " +
                Services.legacy.languageManager.getText(Language.Keys.reload),
            buttons: [
                {
                    scope: this,
                    text: Services.legacy.languageManager.getText(
                        Language.Keys.btn_reload
                    ),
                    action: function() {
                        Services.game.reload();
                    },
                    logData: {
                        name: "freeRoundsReload",
                        button: "reload"
                    },
                    buttonId: "Reload"
                }
            ]
        });
    },

    /**
     * Request a dialog from Dialog Manager.
     *
     * @param {IDialogConfig} config Dialog config.
     * @returns {void}
     */
    requestDialog: function(config) {
        Services.dialog.show(config);
    }
};

/* global Sys, Core, Services, Language, Resources, Utils */
Sys.ns("Core.Slots");

Core.Slots.FreeRoundsController = Sys.extend(
    Core.Controller,
    Core.Slots.FreeRoundsController,
    "Core.Slots.FreeRoundsController"
);
